// Phase 4d award intake — the Launch-screen panel that turns an uploaded award
// document into a real award comparison.
//
// Flow: pick a file -> upload to the pre-award's zou project (tesoro) ->
// ProcessBatch -> register a global processing job -> Begin Award Comparison
// with the processed doc's id. zou then runs baltigo ExtractAwardedContractV2
// server-side, persists the per-value source references, and compares off the
// real extraction.
//
// Processing state lives in the global jobs store (ProcessingJobsHost polls
// GetBatchStatus), not component state: navigating away, switching browser
// tabs, or reloading the page no longer orphans the batch — re-entering this
// panel resumes from wherever the job is.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { IntelligenceTypes } from '@mono/proto-clients';
import { SpecraBadge, SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraProgress } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { FileUploader } from '../../../../components/ProjectList/FileUploader';
import { useUploadProjectFiles } from '../../../../hooks/useProjectFiles';
import { proposalProjectApiConnect } from '../../../../lib/api/proposalProjectApi.connect';
import { selectJobForPreAward, useProcessingJobsStore } from '../../../../lib/processing/jobsStore';
import { awardRunWorking, useAwardComparisonRun } from '../hooks';
export function AwardIntakePanel({ preAwardProjectId, zouProjectId, onComparison }) {
    const [files, setFiles] = useState([]);
    const [localPhase, setLocalPhase] = useState('select');
    // Resume any tracked job for this workspace — survives unmounts/reloads.
    const job = useProcessingJobsStore((s)=>selectJobForPreAward(s, preAwardProjectId));
    const registerJob = useProcessingJobsStore((s)=>s.registerJob);
    const removeJob = useProcessingJobsStore((s)=>s.removeJob);
    const upload = useUploadProjectFiles();
    const comparison = useAwardComparisonRun(preAwardProjectId);
    const run = comparison.data;
    const comparing = comparison.start.isPending || awardRunWorking(run);
    const observedRun = useRef('');
    const changeSetId = run?.extraction?.extraction.case === 'awardImport' ? run.extraction.extraction.value.changeSetId : '';
    const cacheInfo = run?.extraction?.extraction.case === 'awardImport' ? run.extraction.extraction.value.cacheInfo : undefined;
    const availableChangeSetId = changeSetId || comparison.lastCompletedChangeSetId;
    const showRun = run && (!job || comparing || run.sourceArtifactIds.includes(job.meta?.awardDocId ?? ''));
    useEffect(()=>{
        if (awardRunWorking(run)) observedRun.current = run?.id ?? '';
        if (run && changeSetId && observedRun.current === run.id) {
            observedRun.current = '';
            if (job && run.sourceArtifactIds.includes(job.meta?.awardDocId ?? '')) removeJob(job.batchId);
            onComparison(changeSetId);
        }
    }, [
        run,
        changeSetId,
        job,
        removeJob,
        onComparison
    ]);
    const processing = !comparing && job?.status === 'processing';
    const ready = job?.status === 'ready' && !comparing;
    const handleUploadAndProcess = async ()=>{
        if (files.length === 0) return;
        setLocalPhase('uploading');
        try {
            const result = await upload.mutateAsync({
                projectId: zouProjectId,
                files
            });
            const ids = result.files.map((f)=>f.attachmentId).filter(Boolean);
            if (ids.length === 0) {
                setLocalPhase('select');
                toast.error('Award upload failed');
                return;
            }
            const batch = await proposalProjectApiConnect.processBatch(zouProjectId, ids, {
                awardIntakeOnly: true
            });
            // Replace any previous (failed) attempt for this workspace.
            if (job) removeJob(job.batchId);
            registerJob({
                batchId: batch.batchId,
                zouProjectId,
                kind: 'award-intake',
                label: 'Processing award document',
                // ?screen=launch: the tray's View must land ON the Launch screen
                // (this panel), not the cockpit's default Readiness board.
                link: `/delivery/pre-award/${preAwardProjectId}/v2?screen=launch`,
                meta: {
                    preAwardProjectId,
                    awardDocId: ids[0],
                    artifactIds: ids
                }
            });
            setLocalPhase('select');
        } catch (err) {
            setLocalPhase('select');
            toast.error(err instanceof Error ? err.message : 'Award upload/process failed');
        }
    };
    const handleBegin = (skipExtractionCache = false)=>{
        const awardDocId = skipExtractionCache ? run?.sourceArtifactIds[0] : job?.meta?.awardDocId;
        if (!awardDocId) return;
        comparison.start.mutate({
            awardDocId,
            awardArtifactIds: skipExtractionCache ? run?.sourceArtifactIds ?? [
                awardDocId
            ] : job?.meta?.artifactIds ?? [
                awardDocId
            ],
            skipExtractionCache
        }, {
            onSuccess: (started)=>{
                observedRun.current = started.id;
            },
            onError: (err)=>{
                toast.error(err instanceof Error ? err.message : 'Failed to begin award comparison');
            }
        });
    };
    return /*#__PURE__*/ _jsxs(SpecraCard, {
        "data-testid": Testids.preAwardV2.awardIntakePanel,
        children: [
            /*#__PURE__*/ _jsx(SpecraCardHeader, {
                children: /*#__PURE__*/ _jsx(SpecraCardTitle, {
                    children: "Award document"
                })
            }),
            /*#__PURE__*/ _jsxs(SpecraCardContent, {
                className: "space-y-3 text-sm",
                children: [
                    !processing && !ready && !comparing && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-muted-foreground",
                                children: "Upload the awarded contract document. It's processed into source-anchored fields, then compared against the frozen bid baseline."
                            }),
                            job?.status === 'failed' && /*#__PURE__*/ _jsx("p", {
                                className: "text-destructive",
                                children: job.error ?? 'Processing failed — try again.'
                            }),
                            /*#__PURE__*/ _jsx(FileUploader, {
                                files: files,
                                onChange: setFiles,
                                disabled: localPhase === 'uploading',
                                inputTestid: Testids.preAwardV2.awardUploadInput
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                disabled: files.length === 0 || localPhase === 'uploading',
                                onClick: handleUploadAndProcess,
                                "data-testid": Testids.preAwardV2.awardUploadSubmit,
                                children: localPhase === 'uploading' ? 'Uploading…' : 'Upload and process'
                            })
                        ]
                    }),
                    comparison.start.isPending && !run && /*#__PURE__*/ _jsx("output", {
                        children: "Starting award extraction…"
                    }),
                    showRun && run && /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2",
                        "data-testid": Testids.preAwardV2.awardExtractionRun,
                        "data-run-id": run.id,
                        children: [
                            /*#__PURE__*/ _jsx("output", {
                                className: "block",
                                children: run.currentStep
                            }),
                            cacheInfo && /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-1",
                                "data-testid": Testids.preAwardV2.awardExtractionCache,
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraBadge, {
                                        variant: "secondary",
                                        children: cacheInfo.hit ? 'Loaded from cache' : 'Fresh extraction'
                                    }),
                                    /*#__PURE__*/ _jsxs("details", {
                                        className: "text-xs text-muted-foreground",
                                        children: [
                                            /*#__PURE__*/ _jsx("summary", {
                                                children: "Extraction details"
                                            }),
                                            /*#__PURE__*/ _jsxs("p", {
                                                children: [
                                                    cacheInfo.model,
                                                    " · ",
                                                    cacheInfo.extractedAt
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("p", {
                                                children: [
                                                    "Source MD5: ",
                                                    cacheInfo.sourceMd5
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("p", {
                                                children: [
                                                    "Extraction MD5: ",
                                                    cacheInfo.extractionMd5
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            }),
                            comparing && /*#__PURE__*/ _jsx("p", {
                                className: "text-muted-foreground",
                                children: "You can leave this page. Extraction continues, and this workspace will show its result when you return."
                            }),
                            run.errorMessage && /*#__PURE__*/ _jsx("p", {
                                className: "text-destructive",
                                children: run.errorMessage
                            }),
                            (run.state === IntelligenceTypes.IntelligenceRunState.QUEUED || run.state === IntelligenceTypes.IntelligenceRunState.EXTRACTING) && /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "outline",
                                "data-testid": Testids.preAwardV2.awardExtractionCancel,
                                disabled: comparison.cancel.isPending,
                                onClick: ()=>comparison.cancel.mutate(run.id, {
                                        onError: (err)=>toast.error(err instanceof Error ? err.message : 'Could not cancel extraction')
                                    }),
                                children: "Cancel extraction"
                            }),
                            availableChangeSetId && /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                onClick: ()=>onComparison(availableChangeSetId),
                                children: "Open award comparison"
                            }),
                            !comparing && run.sourceArtifactIds.length > 0 && /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "outline",
                                onClick: ()=>handleBegin(true),
                                children: "Re-extract"
                            })
                        ]
                    }),
                    comparison.error && /*#__PURE__*/ _jsxs("p", {
                        className: "text-destructive",
                        children: [
                            "Could not load extraction status. ",
                            comparison.error.message
                        ]
                    }),
                    processing && /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2",
                        "data-testid": Testids.preAwardV2.awardProcessing,
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-muted-foreground",
                                children: "Processing award document — extracting source-anchored fields…"
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraProgress, {
                                        value: job?.percent ?? 0,
                                        "aria-label": "Award document processing progress",
                                        className: "h-2 flex-1",
                                        "data-testid": Testids.preAwardV2.awardProgress,
                                        "data-percent": job?.percent ?? 0
                                    }),
                                    /*#__PURE__*/ _jsxs("span", {
                                        className: "text-xs tabular-nums text-muted-foreground",
                                        children: [
                                            job?.percent ?? 0,
                                            "%"
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-xs text-muted-foreground",
                                children: "You can navigate away — progress continues in the tray at the bottom right."
                            })
                        ]
                    }),
                    ready && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(SpecraBadge, {
                                variant: "outline",
                                "data-testid": Testids.preAwardV2.awardReady,
                                children: "Award document ready"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-muted-foreground",
                                children: "Extract the awarded fields and compare them against the frozen bid baseline. Progress is saved so you can return while it runs."
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "secondary",
                                disabled: comparing || comparison.isLoading || !!comparison.error,
                                onClick: ()=>handleBegin(),
                                "data-testid": Testids.preAwardV2.beginComparisonButton,
                                children: "Begin Award Comparison"
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
